import { Link } from 'react-router-dom';
import {
  Home,
  Briefcase,
  Plug,
  Settings as SettingsIcon,
  CreditCard,
  Shield,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  Users,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useAuthRoles } from '../../../../hooks/useAuthRoles';
import { Button } from '@/components/ui/atoms/Button';
import { SidebarItem } from './SidebarItem';
import { SidebarGroup } from './SidebarGroup';
import { PlanChip } from './PlanChip';
import { Tag } from '../../atoms/Tag';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export const Sidebar = ({ collapsed, onToggleCollapsed }: SidebarProps) => {
  const { t } = useTranslation('common');
  const { isAdmin } = useAuthRoles();
  const { user, logout } = useAuth0();
  const brandLogoSrc = '/logo/dark-logo.png';
  const brandName = 'Invirtus';

  return (
    <aside
      className={`hidden md:flex fixed inset-y-0 left-0 z-40 flex-col border-r border-light-lila/40 bg-light transition-[width] duration-200 ease-out ${
        collapsed ? 'w-14' : 'w-60'
      }`}
      aria-label={t('nav.primary_nav')}
    >
      {/* Brand / logo header — same h-14 baseline as Topbar, generous indent */}
      <div
        className={`flex items-center border-b border-light-lila/40 ${
          collapsed ? 'h-14 justify-center' : 'h-14 px-5'
        }`}
      >
        <Link
          to="/home"
          className="flex items-center gap-2.5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 rounded-md"
          aria-label={`${brandName} home`}
        >
          <motion.img
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            src={brandLogoSrc}
            alt=""
            className="h-8 w-auto max-w-16 shrink-0 object-contain"
          />
          {!collapsed && (
            <span className="text-dark-invirtus text-[17px] leading-none font-semibold font-heading tracking-tight">
              {brandName}
            </span>
          )}
        </Link>
      </div>

      {/* Nav body */}
      <nav
        className={`flex-1 overflow-y-auto overflow-x-hidden ${
          collapsed ? 'px-1.5 py-2' : 'px-2 py-2'
        }`}
      >
        <SidebarGroup label={t('nav.section_primary')} collapsed={collapsed}>
          <SidebarItem to="/home" icon={<Home />} label={t('nav.home')} matchPaths={['/home']} collapsed={collapsed} />
          <SidebarItem
            to="/jobs"
            icon={<Briefcase />}
            label={t('nav.jobs')}
            matchPaths={['/dashboard', '/jobs', '/job']}
            collapsed={collapsed}
          />
          <SidebarItem
            to="/talent-pool"
            icon={<Users />}
            label={t('nav.talent_pool')}
            matchPaths={['/talent-pool']}
            collapsed={collapsed}
          />
          <SidebarItem
            to="/agents"
            icon={<Sparkles />}
            label={t('nav.agents')}
            matchPaths={['/agents']}
            collapsed={collapsed}
            badge={
              <Tag intent="primary" variant="subtle" className="uppercase" size="sm">
                {t('nav.badge_new')}
              </Tag>
            }
          />
        </SidebarGroup>

        <SidebarGroup label={t('nav.section_workspace')} collapsed={collapsed}>
          <SidebarItem
            to="/integrations"
            icon={<Plug />}
            label={t('nav.integrations')}
            matchPaths={['/integrations']}
            collapsed={collapsed}
          />
          <SidebarItem
            to="/settings"
            icon={<SettingsIcon />}
            label={t('nav.settings')}
            matchPaths={['/settings']}
            collapsed={collapsed}
          />
          <SidebarItem
            to="/billing"
            icon={<CreditCard />}
            label={t('nav.billing')}
            matchPaths={['/billing']}
            collapsed={collapsed}
          />
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup label={t('nav.section_admin')} collapsed={collapsed}>
            <SidebarItem
              to="/admin"
              icon={<Shield />}
              label={t('nav.admin')}
              matchPaths={['/admin']}
              collapsed={collapsed}
            />
          </SidebarGroup>
        )}
      </nav>

      {/* Plan chip — sits above the divider so the footer area only holds
          collapse toggle + account chip. */}
      <div className={collapsed ? 'px-1.5 pb-2' : 'px-2 pb-2'}>
        <PlanChip collapsed={collapsed} />
      </div>

      {/* Footer: collapse toggle + user */}
      <div
        className={`border-t border-light-lila/40 ${
          collapsed ? 'flex flex-col items-stretch gap-2 px-1.5 py-2' : 'px-2 py-2 space-y-2'
        }`}
      >
        {/* Collapse toggle — label left, icon right */}
        {collapsed ? (
          <Button
            type="icon"
            intent="muted"
            size="sm"
            icon={<PanelLeftOpen />}
            onClick={onToggleCollapsed}
            title={t('nav.expand_sidebar')}
            aria-label={t('nav.expand_sidebar')}
            className="mx-auto"
          />
        ) : (
          <Button
            type="text"
            intent="muted"
            size="sm"
            label={t('nav.collapse')}
            labelClassName="text-xs font-medium font-body"
            rightIcon={<PanelLeftClose />}
            spreadContent
            onClick={onToggleCollapsed}
            title={t('nav.collapse_sidebar')}
            aria-label={t('nav.collapse_sidebar')}
            className="w-full px-2.5"
          />
        )}

        {/* User chip */}
        <div className={`${collapsed ? 'flex justify-center' : ''}`}>
          {collapsed ? (
            <button
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
              title={user?.email || t('nav.sign_out')}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-purple-soft/30 text-dark-invirtus hover:bg-purple-soft/60 transition-colors"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <span className="text-xs font-bold font-heading">
                  {user?.name
                    ? user.name
                        .split(' ')
                        .map((w) => w.charAt(0))
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)
                    : 'U'}
                </span>
              )}
            </button>
          ) : (
            <div className="flex items-center gap-2 rounded-md p-1.5 hover:bg-dark-invirtus-soft transition-colors">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-soft/40 text-dark-invirtus shrink-0">
                  <span className="text-[10px] font-bold font-heading">
                    {user?.name
                      ? user.name
                          .split(' ')
                          .map((w) => w.charAt(0))
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)
                      : 'U'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-dark-invirtus truncate font-body">
                  {user?.name || 'User'}
                </div>
                <div className="text-[11px] text-dark-invirtus/60 truncate font-body">
                  {user?.email}
                </div>
              </div>
              <Button
                type="icon"
                intent="muted"
                size="xs"
                icon={<LogOut />}
                onClick={() =>
                  logout({ logoutParams: { returnTo: window.location.origin } })
                }
                aria-label={t('nav.sign_out')}
                title={t('nav.sign_out')}
                className="shrink-0"
              />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};
